import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { format } from 'date-fns';
import { MdSchedule, MdPerson, MdCheckCircle, MdEngineering } from 'react-icons/md';

const EngineerVisitStatus = () => {
    const [visit, setVisit] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVisits = async () => {
            try {
                const res = await api.get('/hardware/visits');
                // Find visible visits (scheduled, ongoing, or completed today)
                const activeVisit = res.data.find(v =>
                    v.visit_status === 'scheduled' ||
                    v.visit_status === 'on_the_way' ||
                    v.visit_status === 'working' ||
                    v.visit_status === 'ongoing' ||
                    (v.visit_status === 'work_done' && new Date(v.updated_at).toDateString() === new Date().toDateString()) ||
                    (v.status === 'completed' && new Date(v.updated_at).toDateString() === new Date().toDateString())
                );
                setVisit(activeVisit);
            } catch (error) {
                console.error("Error fetching visits", error);
            } finally {
                setLoading(false);
            }
        };
        fetchVisits();

        // Auto refresh every 30 seconds for real-time updates
        const interval = setInterval(fetchVisits, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading || !visit) return null;

    // Determine Status Colors & Text
    let statusConfig = {
        bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-900',
        iconColor: 'bg-teal-100 text-teal-600',
        title: '👨‍🔧 Incoming Engineer Visit', message: 'An engineer has been scheduled to visit your center.'
    };

    // Priority Check using Timestamps (Absolute Truth)
    if (visit.check_out_time || visit.status === 'completed' || visit.visit_status === 'work_done') {
        statusConfig = {
            bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900',
            iconColor: 'bg-green-100 text-green-600',
            title: '✅ Visit Completed',
            message: `Engineer has completed the visit on ${format(new Date(visit.check_out_time || visit.updated_at), 'dd MMM, hh:mm a')}.`
        };
    } else if (visit.check_in_time || visit.status === 'ongoing') {
        // If checked in, they are working
        statusConfig = {
            bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900',
            iconColor: 'bg-amber-100 text-amber-600',
            title: '🚧 Working Started In Center',
            message: 'Engineer is currently working at your center.'
        };
    } else if (visit.departed_at || visit.status === 'on_the_way' || visit.visit_status === 'on_the_way') {
        // If departed but not checked in -> En Route
        statusConfig = {
            bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900',
            iconColor: 'bg-blue-100 text-blue-600',
            title: '🚗 Engineer En Route',
            message: 'The engineer has departed and is on the way to your center.'
        };
    } else {
        // Scheduled
        statusConfig = {
            bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-800',
            iconColor: 'bg-indigo-100 text-indigo-600',
            title: '📅 Visit Scheduled',
            message: `Engineer ${visit.engineer?.name || 'Assigned'} is scheduled to visit on ${format(new Date(visit.scheduled_at), 'dd MMM yyyy')}.`
        };
    }


    return (
        <div className={`rounded-lg border p-3 shadow-sm animate-in fade-in slide-in-from-top-4 duration-500 mb-4 ${statusConfig.bg} ${statusConfig.border}`}>
            <div className="flex items-center gap-3 mb-2.5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm border border-white/50 ${statusConfig.iconColor}`}>
                    {visit.status === 'completed' ? <MdCheckCircle size={20} /> : <MdEngineering size={20} />}
                </div>
                <div>
                    <h4 className={`font-bold text-xs uppercase tracking-wide flex items-center gap-2 ${statusConfig.text}`}>
                        {statusConfig.title}
                        {visit.status === 'ongoing' && <span className="flex h-1.5 w-1.5 relative"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span></span>}
                    </h4>
                    <p className={`text-[11px] font-medium opacity-80 ${statusConfig.text}`}>{statusConfig.message}</p>
                </div>
            </div>

            <div className="ml-0.5 pt-2 border-t border-black/5 pl-1">
                <div className="flex justify-between items-start gap-4">
                    <div>
                        <p className={`text-[9px] uppercase font-bold mb-0.5 opacity-60 ${statusConfig.text}`}>Engineer</p>
                        <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <MdPerson size={14} className="opacity-50" />
                            {visit.engineer ? visit.engineer.name : 'Assigned Engineer'}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className={`text-[9px] uppercase font-bold mb-0.5 opacity-60 ${statusConfig.text}`}>
                            {visit.status === 'completed' ? 'Completion Time' : 'Scheduled Arrival'}
                        </p>
                        <p className="text-xs font-bold text-slate-700">
                            {visit.status === 'completed'
                                ? (visit.check_out_time ? format(new Date(visit.check_out_time), 'hh:mm a') : 'Just now')
                                : (visit.scheduled_at ? format(new Date(visit.scheduled_at), 'dd MMM, hh:mm a') : 'N/A')
                            }
                        </p>
                    </div>
                </div>

                {visit.remarks && visit.status !== 'completed' && (
                    <div className="mt-2.5 bg-white/60 p-2 rounded-md border border-black/5">
                        <p className={`text-[9px] uppercase font-bold mb-0.5 opacity-70 ${statusConfig.text}`}>Visit Purpose</p>
                        <p className="text-[11px] text-slate-700 italic leading-snug">"{visit.remarks}"</p>
                    </div>
                )}

                {visit.work_description && (visit.visit_status === 'work_done' || visit.status === 'completed') && (
                    <div className="mt-2.5 bg-white/60 p-2 rounded-md border border-black/5">
                        <p className="text-[9px] uppercase font-bold mb-0.5 text-green-700">Work Report</p>
                        <p className="text-[11px] text-slate-700 leading-snug">"{visit.work_description}"</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EngineerVisitStatus;
